<template>
  <div class="users-page">
    <!-- Page Header -->
    <div class="page-header">
      <div class="header-content">
        <h2>Gestion des Utilisateurs</h2>
        <p>Gérez les comptes utilisateurs et leurs permissions</p>
      </div>
      <div class="header-actions">
        <button 
          class="btn btn-primary"
          @click="showAddUserModal = true"
        >
          <i class="fas fa-user-plus"></i>
          Ajouter Utilisateur
        </button>
      </div>
    </div>

    <!-- Filters and Search -->
    <div class="filters-section">
      <div class="search-box">
        <div class="search-input-wrapper">
          <i class="fas fa-search"></i>
          <input
            v-model="filters.search"
            type="text"
            placeholder="Rechercher par nom, prénom ou email..."
            class="search-input"
            @input="debouncedSearch"
          />
          <button 
            v-if="filters.search"
            @click="clearSearch"
            class="clear-search"
          >
            <i class="fas fa-times"></i>
          </button>
        </div>
      </div>

      <div class="filter-controls">
        <div class="filter-wrapper" :class="getRoleClass">
          <div class="filter-icon">
            <i class="fas fa-user-tag"></i>
          </div>
          <select v-model="filters.role" @change="applyFilters" class="filter-select premium-select">
            <option value="">Tous les rôles</option>
            <option v-for="role in roles" :key="role.value" :value="role.value">
              {{ getRoleIcon(role.value) }} {{ role.label }}
            </option>
          </select>
          <div class="filter-arrow">
            <i class="fas fa-chevron-down"></i>
          </div>
        </div>

        <div class="filter-wrapper" :class="getStatusClass">
          <div class="filter-icon">
            <i class="fas fa-toggle-on"></i>
          </div>
          <select v-model="filters.actif" @change="applyFilters" class="filter-select premium-select">
            <option value="">Tous les statuts</option>
            <option value="true">✅ Actifs</option>
            <option value="false">❌ Inactifs</option>
          </select>
          <div class="filter-arrow">
            <i class="fas fa-chevron-down"></i>
          </div>
        </div>

        <button 
          @click="resetFilters"
          class="btn btn-secondary reset-btn"
          :disabled="!hasActiveFilters"
        >
          <i class="fas fa-undo"></i>
          Réinitialiser
        </button>
      </div>
    </div>

    <!-- Bulk Actions -->
    <div v-if="selectedUsers.length > 0" class="bulk-actions">
      <div class="bulk-info">
        {{ selectedUsers.length }} utilisateur(s) sélectionné(s)
      </div>
      <div class="bulk-buttons">
        <button 
          @click="bulkAction('activate')"
          class="btn btn-sm btn-success"
        >
          <i class="fas fa-check"></i>
          Activer
        </button>
        <button 
          @click="bulkAction('deactivate')"
          class="btn btn-sm btn-warning"
        >
          <i class="fas fa-ban"></i>
          Désactiver
        </button>
        <button 
          @click="bulkAction('delete')"
          class="btn btn-sm btn-danger"
        >
          <i class="fas fa-trash"></i>
          Supprimer
        </button>
      </div>
    </div>

    <!-- Users Table -->
    <div class="users-table-container">
      <div v-if="loading" class="loading-state">
        <div class="loading-spinner"></div>
        <p>Chargement des utilisateurs...</p>
      </div>

      <div v-else-if="users.length === 0" class="empty-state">
        <i class="fas fa-users"></i>
        <h3>Aucun utilisateur trouvé</h3>
        <p>{{ hasActiveFilters ? 'Aucun utilisateur ne correspond à vos critères de recherche.' : 'Commencez par ajouter votre premier utilisateur.' }}</p>
        <button 
          v-if="!hasActiveFilters"
          @click="showAddUserModal = true"
          class="btn btn-primary"
        >
          <i class="fas fa-user-plus"></i>
          Ajouter le premier utilisateur
        </button>
      </div>

      <!-- Vue Desktop: Tableau -->
      <div class="table-responsive desktop-view">
        <table class="table users-table">
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  :checked="allUsersSelected"
                  @change="toggleAllUsers"
                  class="checkbox"
                />
              </th>
              <th>Utilisateur</th>
              <th>Email</th>
              <th>Rôle</th>
              <th>Statut</th>
              <th>Dernière connexion</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr 
              v-for="user in users" 
              :key="user._id"
              :class="{ 'selected': selectedUsers.includes(user._id) }"
            >
              <td>
                <input
                  type="checkbox"
                  :value="user._id"
                  v-model="selectedUsers"
                  class="checkbox"
                />
              </td>
              <td>
                <div class="user-info">
                  <div class="user-avatar">
                    <i class="fas fa-user"></i>
                  </div>
                  <div class="user-details">
                    <div class="user-name">{{ user.prenom }} {{ user.nom }}</div>
                    <div class="user-meta">Créé le {{ formatDate(user.dateCreation) }}</div>
                  </div>
                </div>
              </td>
              <td>
                <span class="user-email">{{ user.email }}</span>
              </td>
              <td>
                <span class="badge" :class="getRoleBadgeClass(user.role)">
                  {{ user.role }}
                </span>
              </td>
              <td>
                <span class="status-badge" :class="user.actif ? 'status-active' : 'status-inactive'">
                  <i :class="user.actif ? 'fas fa-check-circle' : 'fas fa-times-circle'"></i>
                  {{ user.actif ? 'Actif' : 'Inactif' }}
                </span>
              </td>
              <td>
                <span class="last-login">
                  {{ formatTime(user.dernierConnexion) }}
                </span>
              </td>
              <td>
                <div class="action-buttons">
                  <button 
                    @click="editUser(user)"
                    class="btn-action btn-edit"
                    title="Modifier"
                  >
                    <i class="fas fa-edit"></i>
                  </button>
                  <button 
                    @click="changePassword(user)"
                    class="btn-action btn-password"
                    title="Modifier le mot de passe"
                  >
                    <i class="fas fa-key"></i>
                  </button>
                  <button 
                    @click="toggleUserStatus(user)"
                    class="btn-action"
                    :class="user.actif ? 'btn-warning' : 'btn-success'"
                    :title="user.actif ? 'Désactiver' : 'Activer'"
                  >
                    <i :class="user.actif ? 'fas fa-ban' : 'fas fa-check'"></i>
                  </button>
                  <button 
                    @click="deleteUser(user)"
                    class="btn-action btn-danger"
                    title="Supprimer"
                  >
                    <i class="fas fa-trash"></i>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Vue Mobile: Cartes -->
      <div class="mobile-view">
        <div class="select-all-mobile">
          <label class="checkbox-label">
            <input
              type="checkbox"
              :checked="allUsersSelected"
              @change="toggleAllUsers"
              class="checkbox"
            />
            <span>Tout sélectionner</span>
          </label>
        </div>
        
        <div class="users-cards">
          <div 
            v-for="user in users" 
            :key="user._id"
            class="user-card"
            :class="{ 'selected': selectedUsers.includes(user._id) }"
          >
            <div class="card-header">
              <label class="checkbox-label">
                <input
                  type="checkbox"
                  :value="user._id"
                  v-model="selectedUsers"
                  class="checkbox"
                />
              </label>
              <div class="user-avatar">
                <i class="fas fa-user"></i>
              </div>
              <div class="user-main-info">
                <div class="user-name">{{ user.prenom }} {{ user.nom }}</div>
                <div class="user-email">{{ user.email }}</div>
              </div>
              <span class="status-indicator" :class="user.actif ? 'active' : 'inactive'"></span>
            </div>
            
            <div class="card-body">
              <div class="info-row">
                <span class="info-label">
                  <i class="fas fa-user-tag"></i>
                  Rôle
                </span>
                <span class="badge" :class="getRoleBadgeClass(user.role)">
                  {{ user.role }}
                </span>
              </div>
              
              <div class="info-row">
                <span class="info-label">
                  <i class="fas fa-toggle-on"></i>
                  Statut
                </span>
                <span class="status-badge" :class="user.actif ? 'status-active' : 'status-inactive'">
                  <i :class="user.actif ? 'fas fa-check-circle' : 'fas fa-times-circle'"></i>
                  {{ user.actif ? 'Actif' : 'Inactif' }}
                </span>
              </div>
              
              <div class="info-row">
                <span class="info-label">
                  <i class="fas fa-clock"></i>
                  Dernière connexion
                </span>
                <span class="last-login">{{ formatTime(user.dernierConnexion) }}</span>
              </div>
              
              <div class="info-row">
                <span class="info-label">
                  <i class="fas fa-calendar"></i>
                  Créé le
                </span>
                <span class="user-meta">{{ formatDate(user.dateCreation) }}</span>
              </div>
            </div>
            
            <div class="card-actions">
              <button 
                @click="editUser(user)"
                class="btn-card-action btn-edit"
              >
                <i class="fas fa-edit"></i>
                Modifier
              </button>
              <button 
                @click="changePassword(user)"
                class="btn-card-action btn-password"
              >
                <i class="fas fa-key"></i>
                Mot de passe
              </button>
              <button 
                @click="toggleUserStatus(user)"
                class="btn-card-action"
                :class="user.actif ? 'btn-warning' : 'btn-success'"
              >
                <i :class="user.actif ? 'fas fa-ban' : 'fas fa-check'"></i>
                {{ user.actif ? 'Désactiver' : 'Activer' }}
              </button>
              <button 
                @click="deleteUser(user)"
                class="btn-card-action btn-danger"
              >
                <i class="fas fa-trash"></i>
                Supprimer
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Pagination -->
    <div v-if="totalPages > 1" class="pagination-container">
      <div class="pagination-info">
        Affichage de {{ (currentPage - 1) * 10 + 1 }} à {{ Math.min(currentPage * 10, totalUsers) }} sur {{ totalUsers }} utilisateurs
      </div>
      <div class="pagination">
        <button 
          @click="changePage(currentPage - 1)"
          :disabled="currentPage === 1"
          class="btn btn-sm btn-secondary"
        >
          <i class="fas fa-chevron-left"></i>
          Précédent
        </button>
        
        <div class="page-numbers">
          <button
            v-for="page in visiblePages"
            :key="page"
            @click="changePage(page)"
            class="btn btn-sm"
            :class="page === currentPage ? 'btn-primary' : 'btn-secondary'"
          >
            {{ page }}
          </button>
        </div>

        <button 
          @click="changePage(currentPage + 1)"
          :disabled="currentPage === totalPages"
          class="btn btn-sm btn-secondary"
        >
          Suivant
          <i class="fas fa-chevron-right"></i>
        </button>
      </div>
    </div>

    <!-- Modals -->
    <AddUserModal 
      v-if="showAddUserModal"
      @close="showAddUserModal = false"
      @user-added="handleUserAdded"
    />

    <EditUserModal 
      v-if="showEditUserModal"
      :user="selectedUser"
      @close="showEditUserModal = false"
      @user-updated="handleUserUpdated"
    />

    <ChangePasswordModal
      v-if="showChangePasswordModal"
      :user="selectedUser"
      @close="showChangePasswordModal = false"
      @password-changed="handlePasswordChanged"
    />

    <ConfirmModal
      v-if="showConfirmModal"
      :title="confirmModal.title"
      :message="confirmModal.message"
      :type="confirmModal.type"
      @confirm="confirmModal.onConfirm"
      @cancel="showConfirmModal = false"
    />
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue'
import { useStore } from 'vuex'
import { useToast } from 'vue-toastification'
import AddUserModal from '../components/AddUserModal.vue'
import EditUserModal from '../components/EditUserModal.vue'
import ChangePasswordModal from '../components/ChangePasswordModal.vue'
import ConfirmModal from '../components/ConfirmModal.vue'

export default {
  name: 'Users',
  components: {
    AddUserModal,
    EditUserModal,
    ChangePasswordModal,
    ConfirmModal
  },
  setup() {
    const store = useStore()
    const toast = useToast()

    const showAddUserModal = ref(false)
    const showEditUserModal = ref(false)
    const showChangePasswordModal = ref(false)
    const showConfirmModal = ref(false)
    const selectedUser = ref(null)
    const selectedUsers = ref([])
    const searchTimeout = ref(null)

    const filters = ref({
      search: '',
      role: '',
      actif: ''
    })

    const confirmModal = ref({
      title: '',
      message: '',
      type: 'danger',
      onConfirm: null
    })

    const users = computed(() => store.getters['users/users'])
    const totalUsers = computed(() => store.getters['users/totalUsers'])
    const currentPage = computed(() => store.getters['users/currentPage'])
    const totalPages = computed(() => store.getters['users/totalPages'])
    const loading = computed(() => store.getters['users/loading'])
    const roles = computed(() => store.getters['users/roles'])

    const hasActiveFilters = computed(() => {
      return filters.value.search || filters.value.role || filters.value.actif
    })

    const allUsersSelected = computed(() => {
      return users.value.length > 0 && selectedUsers.value.length === users.value.length
    })

    const visiblePages = computed(() => {
      const pages = []
      const start = Math.max(1, currentPage.value - 2)
      const end = Math.min(totalPages.value, start + 4)
      
      for (let i = start; i <= end; i++) {
        pages.push(i)
      }
      
      return pages
    })

    const loadUsers = async (page = 1) => {
      try {
        await store.dispatch('users/fetchUsers', {
          page,
          limit: 10,
          ...filters.value
        })
      } catch (error) {
        toast.error('Erreur lors du chargement des utilisateurs')
      }
    }

    const debouncedSearch = () => {
      if (searchTimeout.value) {
        clearTimeout(searchTimeout.value)
      }
      
      searchTimeout.value = setTimeout(() => {
        applyFilters()
      }, 500)
    }

    const applyFilters = () => {
      selectedUsers.value = []
      loadUsers(1)
    }

    const resetFilters = () => {
      filters.value = {
        search: '',
        role: '',
        actif: ''
      }
      applyFilters()
    }

    const clearSearch = () => {
      filters.value.search = ''
      applyFilters()
    }

    const changePage = (page) => {
      if (page >= 1 && page <= totalPages.value) {
        loadUsers(page)
      }
    }

    const toggleAllUsers = () => {
      if (allUsersSelected.value) {
        selectedUsers.value = []
      } else {
        selectedUsers.value = users.value.map(user => user._id)
      }
    }

    const editUser = (user) => {
      selectedUser.value = user
      showEditUserModal.value = true
    }

    const changePassword = (user) => {
      selectedUser.value = user
      showChangePasswordModal.value = true
    }

    const handlePasswordChanged = () => {
      showChangePasswordModal.value = false
      selectedUser.value = null
      toast.success('Mot de passe modifié avec succès')
    }

    const toggleUserStatus = (user) => {
      const action = user.actif ? 'désactiver' : 'activer'
      
      confirmModal.value = {
        title: `${action.charAt(0).toUpperCase() + action.slice(1)} l'utilisateur`,
        message: `Êtes-vous sûr de vouloir ${action} ${user.prenom} ${user.nom} ?`,
        type: user.actif ? 'warning' : 'success',
        onConfirm: async () => {
          try {
            await store.dispatch('users/updateUser', {
              id: user._id,
              userData: { actif: !user.actif }
            })
            toast.success(`Utilisateur ${action} avec succès`)
            showConfirmModal.value = false
          } catch (error) {
            toast.error(`Erreur lors de la ${action}`)
          }
        }
      }
      
      showConfirmModal.value = true
    }

    const deleteUser = (user) => {
      // Message spécial pour les techniciens
      const isTechnicien = user.role === 'Technicien'
      const warningMessage = isTechnicien 
        ? `Êtes-vous sûr de vouloir supprimer définitivement ${user.prenom} ${user.nom} ? 
           
⚠️ Si ce technicien a des produits affectés, ils seront automatiquement retournés au stock.

Cette action est irréversible.`
        : `Êtes-vous sûr de vouloir supprimer définitivement ${user.prenom} ${user.nom} ? Cette action est irréversible.`

      confirmModal.value = {
        title: 'Supprimer l\'utilisateur',
        message: warningMessage,
        type: 'danger',
        onConfirm: async () => {
          try {
            const result = await store.dispatch('users/deleteUser', user._id)
            
            // Message de succès avec info sur les produits retournés
            if (result.produitsRetournes > 0) {
              toast.success(`Utilisateur supprimé avec succès. ${result.produitsRetournes} produit(s) retourné(s) au stock.`)
            } else {
              toast.success('Utilisateur supprimé avec succès')
            }
            
            showConfirmModal.value = false
            selectedUsers.value = selectedUsers.value.filter(id => id !== user._id)
          } catch (error) {
            toast.error('Erreur lors de la suppression')
          }
        }
      }
      
      showConfirmModal.value = true
    }

    const bulkAction = (action) => {
      const actionLabels = {
        activate: 'activer',
        deactivate: 'désactiver',
        delete: 'supprimer'
      }
      
      const actionLabel = actionLabels[action]
      
      confirmModal.value = {
        title: `${actionLabel.charAt(0).toUpperCase() + actionLabel.slice(1)} les utilisateurs`,
        message: `Êtes-vous sûr de vouloir ${actionLabel} ${selectedUsers.value.length} utilisateur(s) ?`,
        type: action === 'delete' ? 'danger' : 'warning',
        onConfirm: async () => {
          try {
            await store.dispatch('users/bulkAction', {
              action,
              userIds: selectedUsers.value
            })
            toast.success(`Utilisateurs ${actionLabel}s avec succès`)
            showConfirmModal.value = false
            selectedUsers.value = []
          } catch (error) {
            toast.error(`Erreur lors de l'action en lot`)
          }
        }
      }
      
      showConfirmModal.value = true
    }

    const handleUserAdded = () => {
      showAddUserModal.value = false
      loadUsers(currentPage.value)
      toast.success('Utilisateur ajouté avec succès')
    }

    const handleUserUpdated = () => {
      showEditUserModal.value = false
      selectedUser.value = null
      toast.success('Utilisateur mis à jour avec succès')
    }

    const formatDate = (dateString) => {
      return new Date(dateString).toLocaleDateString('fr-FR')
    }

    const formatTime = (dateString) => {
      if (!dateString) return 'Jamais'
      const date = new Date(dateString)
      const now = new Date()
      const diff = now - date
      
      const minutes = Math.floor(diff / 60000)
      const hours = Math.floor(diff / 3600000)
      const days = Math.floor(diff / 86400000)
      
      if (minutes < 1) return 'À l\'instant'
      if (minutes < 60) return `Il y a ${minutes} min`
      if (hours < 24) return `Il y a ${hours}h`
      if (days < 7) return `Il y a ${days} jour(s)`
      
      return date.toLocaleDateString('fr-FR')
    }

    const getRoleBadgeClass = (role) => {
      const classes = {
        'Admin': 'badge-primary',
        'Responsable Planning': 'badge-info',
        'Commercial': 'badge-success',
        'Magasinier': 'badge-warning',
        'Technicien': 'badge-secondary'
      }
      return classes[role] || 'badge-secondary'
    }

    // Classes dynamiques pour les filtres
    const getRoleClass = computed(() => {
      return filters.value.role ? 'has-selection' : ''
    })
    
    const getStatusClass = computed(() => {
      return filters.value.actif ? 'has-selection' : ''
    })

    const getRoleIcon = (role) => {
      const icons = {
        'Admin': '👑',
        'Responsable Planning': '📅',
        'Commercial': '💼',
        'Magasinier': '📦',
        'Technicien': '🔧',
        'Financier': '💰'
      }
      return icons[role] || '👤'
    }

    onMounted(() => {
      loadUsers()
    })

    return {
      showAddUserModal,
      showEditUserModal,
      showChangePasswordModal,
      showConfirmModal,
      selectedUser,
      selectedUsers,
      filters,
      confirmModal,
      users,
      totalUsers,
      currentPage,
      totalPages,
      loading,
      roles,
      hasActiveFilters,
      allUsersSelected,
      visiblePages,
      debouncedSearch,
      applyFilters,
      resetFilters,
      clearSearch,
      changePage,
      toggleAllUsers,
      editUser,
      changePassword,
      handlePasswordChanged,
      toggleUserStatus,
      deleteUser,
      bulkAction,
      handleUserAdded,
      handleUserUpdated,
      formatDate,
      formatTime,
      getRoleBadgeClass,
      
      // Classes dynamiques
      getRoleClass,
      getStatusClass,
      getRoleIcon
    }
  }
}
</script>

<style scoped>
.users-page {
  max-width: 1400px;
  margin: 0 auto;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 2rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid var(--light-gray);
}

.header-content h2 {
  font-size: 1.75rem;
  font-weight: 600;
  color: var(--dark-gray);
  margin-bottom: 0.5rem;
}

.header-content p {
  color: var(--medium-gray);
  margin: 0;
}

.filters-section {
  background: var(--white);
  padding: 1.5rem;
  border-radius: 1rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  margin-bottom: 1.5rem;
  display: flex;
  gap: 1rem;
  align-items: center;
}

.search-box {
  flex: 1;
}

.search-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.search-input-wrapper i {
  position: absolute;
  left: 1rem;
  color: var(--medium-gray);
  z-index: 1;
}

.search-input {
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 3rem;
  border: 2px solid var(--light-gray);
  border-radius: 0.5rem;
  font-size: 0.875rem;
  transition: all 0.2s ease;
}

.search-input:focus {
  outline: none;
  border-color: var(--secondary-blue);
  box-shadow: 0 0 0 3px rgba(0, 70, 255, 0.1);
}

.clear-search {
  position: absolute;
  right: 1rem;
  background: none;
  border: none;
  color: var(--medium-gray);
  cursor: pointer;
  padding: 0.25rem;
}

.filter-controls {
  display: flex;
  gap: 1rem;
  align-items: center;
  flex-wrap: wrap;
}

/* Design professionnel pour les filtres utilisateurs */
.filter-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 251, 255, 0.95) 100%);
  border: 2px solid rgba(0, 27, 183, 0.15);
  border-radius: 16px;
  padding: 0;
  overflow: hidden;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  backdrop-filter: blur(10px);
  box-shadow: 0 4px 20px rgba(0, 27, 183, 0.08);
  min-width: 200px;
}

.filter-wrapper:hover {
  border-color: rgba(0, 27, 183, 0.3);
  box-shadow: 0 8px 32px rgba(0, 27, 183, 0.15);
  transform: translateY(-2px);
}

.filter-wrapper:focus-within {
  border-color: var(--primary-blue);
  box-shadow: 0 0 0 4px rgba(0, 27, 183, 0.1), 0 8px 32px rgba(0, 27, 183, 0.2);
  transform: translateY(-3px);
}

.filter-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, var(--primary-blue) 0%, var(--secondary-blue) 100%);
  color: white;
  font-size: 0.9rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
}

.filter-icon::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(45deg, rgba(255,255,255,0.2), transparent);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.filter-wrapper:hover .filter-icon::before {
  opacity: 1;
}

.premium-select {
  flex: 1;
  padding: 12px 16px;
  border: none;
  background: transparent;
  font-size: 14px;
  font-weight: 600;
  color: var(--primary-blue);
  cursor: pointer;
  outline: none;
  letter-spacing: 0.3px;
  transition: all 0.3s ease;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
}

.premium-select:focus {
  color: var(--secondary-blue);
}

.premium-select option {
  background: white;
  color: #2c3e50;
  padding: 12px 16px;
  font-weight: 500;
  border-radius: 8px;
  margin: 2px 0;
}

.premium-select option:hover {
  background: linear-gradient(135deg, #f8fbff 0%, #e3f2fd 100%);
  color: var(--primary-blue);
}

.premium-select option[value=""] {
  font-weight: 700;
  color: var(--primary-blue);
  background: linear-gradient(135deg, #f8fbff 0%, #ffffff 100%);
}

.filter-arrow {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 40px;
  color: var(--primary-blue);
  font-size: 0.8rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  pointer-events: none;
}

.filter-wrapper:hover .filter-arrow {
  color: var(--secondary-blue);
  transform: translateY(-1px);
}

.filter-wrapper:focus-within .filter-arrow {
  transform: rotate(180deg);
}

/* Indicateur de sélection active pour utilisateurs */
.filter-wrapper.has-selection {
  border-color: var(--primary-blue);
  background: linear-gradient(135deg, rgba(0, 27, 183, 0.08) 0%, rgba(0, 70, 255, 0.08) 100%);
  box-shadow: 0 4px 20px rgba(0, 27, 183, 0.15), 0 0 0 1px rgba(0, 27, 183, 0.1);
}

.filter-wrapper.has-selection .filter-icon {
  background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
  animation: pulse 2s infinite;
  box-shadow: 0 0 0 2px rgba(40, 167, 69, 0.3);
}

.filter-wrapper.has-selection .premium-select {
  color: var(--secondary-blue);
  font-weight: 700;
}

.filter-wrapper.has-selection .filter-arrow {
  color: var(--secondary-blue);
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 2px rgba(40, 167, 69, 0.3), 0 0 0 0 rgba(40, 167, 69, 0.4);
  }
  70% {
    box-shadow: 0 0 0 2px rgba(40, 167, 69, 0.3), 0 0 0 8px rgba(40, 167, 69, 0);
  }
  100% {
    box-shadow: 0 0 0 2px rgba(40, 167, 69, 0.3), 0 0 0 0 rgba(40, 167, 69, 0);
  }
}

/* Badge de compteur pour les filtres actifs */
.filter-wrapper.has-selection::after {
  content: '1';
  position: absolute;
  top: -8px;
  right: -8px;
  width: 20px;
  height: 20px;
  background: linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%);
  color: white;
  border-radius: 50%;
  font-size: 0.7rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(255, 107, 107, 0.4);
  animation: bounceIn 0.5s ease-out;
}

@keyframes bounceIn {
  0% {
    transform: scale(0);
    opacity: 0;
  }
  50% {
    transform: scale(1.2);
    opacity: 1;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

/* Effet de brillance au hover */
.filter-wrapper:hover::after {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent);
  animation: shine 1.5s ease-in-out;
  pointer-events: none;
  z-index: 1;
}

@keyframes shine {
  0% {
    left: -100%;
  }
  100% {
    left: 100%;
  }
}

/* Bouton réinitialiser amélioré */
.reset-btn {
  background: linear-gradient(135deg, #6c757d 0%, #495057 100%);
  border: none;
  border-radius: 12px;
  padding: 12px 20px;
  color: white;
  font-weight: 600;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 15px rgba(108, 117, 125, 0.2);
  backdrop-filter: blur(10px);
}

.reset-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(108, 117, 125, 0.3);
  background: linear-gradient(135deg, #495057 0%, #343a40 100%);
}

.reset-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}

/* Responsive pour les filtres utilisateurs */
@media (max-width: 768px) {
  .filter-controls {
    flex-direction: column;
    gap: 0.75rem;
    width: 100%;
  }
  
  .filter-wrapper {
    min-width: auto;
    width: 100%;
  }
  
  .premium-select {
    font-size: 13px;
  }
  
  .filter-icon {
    width: 35px;
    height: 35px;
    font-size: 0.8rem;
  }
  
  .filter-arrow {
    width: 28px;
    height: 35px;
  }
  
  .reset-btn {
    width: 100%;
    justify-content: center;
  }
}

.bulk-actions {
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  color: var(--white);
  padding: 1rem 1.5rem;
  border-radius: 0.5rem;
  margin-bottom: 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.bulk-info {
  font-weight: 500;
}

.bulk-buttons {
  display: flex;
  gap: 0.5rem;
}

.users-table-container {
  background: var(--white);
  border-radius: 1rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  margin-bottom: 1.5rem;
}

.loading-state,
.empty-state {
  text-align: center;
  padding: 3rem;
  color: var(--medium-gray);
}

.loading-state .loading-spinner {
  width: 40px;
  height: 40px;
  margin: 0 auto 1rem;
}

.empty-state i {
  font-size: 3rem;
  margin-bottom: 1rem;
  opacity: 0.5;
}

.empty-state h3 {
  color: var(--dark-gray);
  margin-bottom: 0.5rem;
}

.users-table {
  margin: 0;
}

.users-table tr.selected {
  background-color: rgba(0, 70, 255, 0.05);
}

.checkbox {
  width: 18px;
  height: 18px;
  cursor: pointer;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.user-avatar {
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  color: var(--white);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
}

.user-details {
  flex: 1;
}

.user-name {
  font-weight: 500;
  color: var(--dark-gray);
  margin-bottom: 0.25rem;
}

.user-meta {
  font-size: 0.75rem;
  color: var(--medium-gray);
}

.user-email {
  color: var(--medium-gray);
  font-size: 0.875rem;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
}

.status-badge.status-active {
  color: var(--success);
}

.status-badge.status-inactive {
  color: var(--error);
}

.last-login {
  font-size: 0.875rem;
  color: var(--medium-gray);
}

.action-buttons {
  display: flex;
  gap: 0.5rem;
}

.btn-action {
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.875rem;
}

.btn-edit {
  background-color: var(--info);
  color: var(--white);
}

.btn-edit:hover {
  background-color: #2563eb;
}

.btn-password {
  background-color: #8b5cf6;
  color: var(--white);
}

.btn-password:hover {
  background-color: #7c3aed;
}

.btn-action.btn-success {
  background-color: var(--success);
  color: var(--white);
}

.btn-action.btn-success:hover {
  background-color: #059669;
}

.btn-action.btn-warning {
  background-color: var(--warning);
  color: var(--white);
}

.btn-action.btn-warning:hover {
  background-color: #d97706;
}

.btn-action.btn-danger {
  background-color: var(--error);
  color: var(--white);
}

.btn-action.btn-danger:hover {
  background-color: #dc2626;
}

.pagination-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: var(--white);
  padding: 1rem 1.5rem;
  border-radius: 0.5rem;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
}

.pagination-info {
  color: var(--medium-gray);
  font-size: 0.875rem;
}

.pagination {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.page-numbers {
  display: flex;
  gap: 0.25rem;
}

/* Vue Desktop/Mobile toggle */
.desktop-view {
  display: block;
}

.mobile-view {
  display: none;
}

/* Styles pour les cartes mobiles */
.select-all-mobile {
  padding: 1rem;
  background: var(--light-gray);
  border-radius: 0.5rem;
  margin-bottom: 1rem;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  cursor: pointer;
  font-weight: 500;
  color: var(--dark-gray);
}

.users-cards {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 0 0.5rem;
}

.user-card {
  background: var(--white);
  border-radius: 1rem;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  transition: all 0.3s ease;
  border: 2px solid transparent;
}

.user-card:hover {
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.12);
  transform: translateY(-2px);
}

.user-card.selected {
  border-color: var(--primary-blue);
  background: rgba(0, 70, 255, 0.02);
}

.card-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  background: linear-gradient(135deg, rgba(0, 27, 183, 0.03) 0%, rgba(0, 70, 255, 0.03) 100%);
  border-bottom: 1px solid var(--light-gray);
  position: relative;
}

.card-header .user-avatar {
  width: 50px;
  height: 50px;
  font-size: 1.25rem;
  flex-shrink: 0;
}

.user-main-info {
  flex: 1;
  min-width: 0;
}

.user-main-info .user-name {
  font-size: 1rem;
  font-weight: 600;
  color: var(--dark-gray);
  margin-bottom: 0.25rem;
}

.user-main-info .user-email {
  font-size: 0.8rem;
  color: var(--medium-gray);
  word-break: break-all;
}

.status-indicator {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  flex-shrink: 0;
}

.status-indicator.active {
  background: var(--success);
  box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.2);
}

.status-indicator.inactive {
  background: var(--error);
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.2);
}

.card-body {
  padding: 1rem;
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.info-row:last-child {
  border-bottom: none;
}

.info-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
  color: var(--medium-gray);
  font-weight: 500;
}

.info-label i {
  width: 16px;
  text-align: center;
  color: var(--primary-blue);
}

.card-actions {
  display: flex;
  gap: 0.5rem;
  padding: 1rem;
  background: var(--light-gray);
  border-top: 1px solid rgba(0, 0, 0, 0.05);
}

.btn-card-action {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem 0.5rem;
  border: none;
  border-radius: 0.5rem;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-card-action.btn-edit {
  background: var(--info);
  color: var(--white);
}

.btn-card-action.btn-edit:hover {
  background: #2563eb;
}

.btn-card-action.btn-password {
  background: #8b5cf6;
  color: var(--white);
}

.btn-card-action.btn-password:hover {
  background: #7c3aed;
}

.btn-card-action.btn-success {
  background: var(--success);
  color: var(--white);
}

.btn-card-action.btn-success:hover {
  background: #059669;
}

.btn-card-action.btn-warning {
  background: var(--warning);
  color: var(--white);
}

.btn-card-action.btn-warning:hover {
  background: #d97706;
}

.btn-card-action.btn-danger {
  background: var(--error);
  color: var(--white);
}

.btn-card-action.btn-danger:hover {
  background: #dc2626;
}

/* Responsive */
@media (max-width: 1024px) {
  .filters-section {
    flex-direction: column;
    align-items: stretch;
    gap: 1.5rem;
  }

  .filter-controls {
    justify-content: flex-start;
    flex-wrap: wrap;
  }
  
  .filter-wrapper {
    min-width: 180px;
  }
}

@media (max-width: 768px) {
  .page-header {
    flex-direction: column;
    gap: 1rem;
  }

  .header-actions {
    width: 100%;
  }

  .header-actions .btn {
    width: 100%;
    justify-content: center;
  }

  .bulk-actions {
    flex-direction: column;
    gap: 1rem;
    text-align: center;
  }

  /* Masquer le tableau, afficher les cartes */
  .desktop-view {
    display: none;
  }

  .mobile-view {
    display: block;
  }

  .pagination-container {
    flex-direction: column;
    gap: 1rem;
  }

  .pagination {
    flex-wrap: wrap;
    justify-content: center;
  }

  .page-numbers {
    order: -1;
    width: 100%;
    justify-content: center;
    margin-bottom: 0.5rem;
  }
}

@media (max-width: 480px) {
  .users-page {
    padding: 0.5rem;
  }

  .filters-section {
    padding: 1rem;
  }

  .card-actions {
    flex-direction: column;
  }

  .btn-card-action {
    width: 100%;
  }

  .bulk-buttons {
    flex-direction: column;
    width: 100%;
  }

  .bulk-buttons .btn {
    width: 100%;
  }
}
</style>
